import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "../../../../lib/utils";
import { microLabel, accentBarSection } from "../../../../lib/design-system";
import {
  createCO,
  updateCO,
} from "../../../../services/program.service";

const BLOOM_LEVELS = [
  { value: "", label: "(unset)" },
  { value: "remember", label: "Remember" },
  { value: "understand", label: "Understand" },
  { value: "apply", label: "Apply" },
  { value: "analyze", label: "Analyze" },
  { value: "evaluate", label: "Evaluate" },
  { value: "create", label: "Create" },
];

const inputBase =
  "w-full px-3 py-2 rounded-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-accent1 transition-colors";

const Field = ({ label, htmlFor, hint, children }) => (
  <div>
    <label
      htmlFor={htmlFor}
      className="block text-xs uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400 mb-1.5"
    >
      {label}
    </label>
    {children}
    {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
  </div>
);

const CoForm = ({ courseId, co, onClose }) => {
  const isEdit = !!co;
  const [form, setForm] = useState({
    code: co?.code || "",
    statement: co?.statement || "",
    bloomLevel: co?.bloomLevel || "",
    topics: (co?.topics || []).join(", "),
    order: co?.order ?? 0,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose(false);
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const payload = {
      code: form.code.trim().toUpperCase(),
      statement: form.statement.trim(),
      bloomLevel: form.bloomLevel,
      topics: form.topics
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
      order: Number(form.order) || 0,
    };

    if (!payload.code || !payload.statement) {
      setError("Code and statement are required.");
      return;
    }
    if (!/^[A-Z0-9]{2,10}$/.test(payload.code)) {
      setError("Code must be 2-10 chars: A-Z, 0-9 only (e.g. CO1).");
      return;
    }

    setSubmitting(true);
    try {
      if (isEdit) {
        await updateCO(co._id, payload);
        toast.success(`${payload.code} updated`);
      } else {
        const result = await createCO(courseId, payload);
        toast.success(
          result?.reactivated
            ? `${payload.code} restored (was previously deleted)`
            : `${payload.code} created`
        );
      }
      onClose(true);
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
        onClick={() => onClose(false)}
      >
        <motion.div
          initial={{ opacity: 0, y: 16, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.98 }}
          transition={{ duration: 0.25 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white dark:bg-gray-800 rounded-sm border border-gray-200 dark:border-gray-700 shadow-md w-full max-w-2xl overflow-hidden max-h-[90vh] flex flex-col"
        >
          <div className="flex items-start justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div>
              <div className={microLabel}>{isEdit ? "Edit CO" : "New CO"}</div>
              <h2 className="mt-1 text-2xl font-black tracking-tight text-gray-900 dark:text-white">
                {isEdit ? co.code : "Add a Course Outcome"}
              </h2>
              <div className={cn(accentBarSection, "mt-3")} />
            </div>
            <button
              onClick={() => onClose(false)}
              className="p-2 rounded-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form
            onSubmit={handleSubmit}
            className="p-6 space-y-5 overflow-y-auto"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Code *" htmlFor="code" hint="A-Z, 0-9. Example: CO1">
                <input
                  id="code"
                  type="text"
                  value={form.code}
                  onChange={(e) =>
                    setField("code", e.target.value.toUpperCase())
                  }
                  placeholder="CO1"
                  required
                  className={inputBase}
                />
              </Field>
              <Field label="Bloom Level" htmlFor="bloomLevel">
                <select
                  id="bloomLevel"
                  value={form.bloomLevel}
                  onChange={(e) => setField("bloomLevel", e.target.value)}
                  className={inputBase}
                >
                  {BLOOM_LEVELS.map((b) => (
                    <option key={b.value} value={b.value}>
                      {b.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <Field label="Statement *" htmlFor="statement">
              <textarea
                id="statement"
                value={form.statement}
                onChange={(e) => setField("statement", e.target.value)}
                rows={4}
                placeholder="Apply linear algebra to ..."
                required
                className={cn(inputBase, "resize-y")}
              />
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field
                label="Topics"
                htmlFor="topics"
                hint="Comma-separated. Optional."
              >
                <input
                  id="topics"
                  type="text"
                  value={form.topics}
                  onChange={(e) => setField("topics", e.target.value)}
                  placeholder="Matrices, Eigenvalues"
                  className={inputBase}
                />
              </Field>
              <Field label="Order" htmlFor="order" hint="Display order">
                <input
                  id="order"
                  type="number"
                  min={0}
                  value={form.order}
                  onChange={(e) => setField("order", e.target.value)}
                  className={inputBase}
                />
              </Field>
            </div>

            {error && (
              <div className="p-3 rounded-sm border border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm">
                {error}
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => onClose(false)}
                className="px-4 py-2 rounded-sm border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 font-semibold transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className={cn(
                  "px-5 py-2 rounded-sm bg-accent1 text-white font-semibold transition-opacity",
                  submitting
                    ? "opacity-60 cursor-not-allowed"
                    : "hover:opacity-90"
                )}
              >
                {submitting ? "Saving…" : isEdit ? "Save changes" : "Create CO"}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CoForm;
